import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { SERVICES } from "@/lib/brand";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Field, NativeSelect } from "@/components/common";

const KEYS = ["date", "orario", "kind", "notturno", "owner_name", "owner_phone", "pickup_address", "city", "pet_name", "species", "weight_kg", "service_type", "notes"];

export const BookingEditDialog = ({ booking: b, open, onOpenChange, onSaved }) => {
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open && b) setF(Object.fromEntries(KEYS.map((k) => [k, b[k] ?? (k === "notturno" ? false : k === "kind" ? "ritiro" : "")]))); }, [open, b]);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const isProm = f.kind === "promemoria";
  const save = async () => {
    if (isProm ? !f.pet_name?.trim() : (!f.date || !f.pickup_address?.trim())) return toast.error(isProm ? "Il titolo è obbligatorio" : "Data e indirizzo sono obbligatori");
    setBusy(true);
    try {
      const payload = { ...f, weight_kg: f.weight_kg === "" ? null : Number(f.weight_kg) };
      if (isProm && !payload.date) payload.date = b.date;
      Object.keys(payload).forEach((k) => { if (payload[k] === "" ) payload[k] = k === "notes" ? "" : payload[k]; });
      const { data } = await api.patch(`/admin/bookings/${b.id}`, payload);
      toast.success("Appuntamento aggiornato");
      onSaved?.(data); onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (!b) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-[#FAF8F5] max-h-[90vh] overflow-y-auto" data-testid="booking-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#0D3B52]">{isProm ? "Modifica appuntamento / promemoria" : "Modifica appuntamento"}</DialogTitle>
          <DialogDescription>{isProm ? "Solo il titolo è obbligatorio." : "Solo l'amministratore può modificare data e dettagli del ritiro."}</DialogDescription>
        </DialogHeader>
        {isProm ? (
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Titolo *" className="sm:col-span-2"><Input value={f.pet_name || ""} onChange={set("pet_name")} className="bg-white" data-testid="booking-edit-title" /></Field>
            <Field label="Giorno"><Input type="date" value={f.date || ""} onChange={set("date")} className="bg-white" data-testid="booking-edit-date" /></Field>
            <Field label="Orario"><Input type="time" value={f.orario || ""} onChange={set("orario")} className="bg-white" data-testid="booking-edit-orario" /></Field>
            <Field label="Indirizzo"><Input value={f.pickup_address || ""} onChange={set("pickup_address")} className="bg-white" data-testid="booking-edit-address" /></Field>
            <Field label="Referente"><Input value={f.owner_name || ""} onChange={set("owner_name")} className="bg-white" data-testid="booking-edit-owner" /></Field>
          </div>
        ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Tipo appuntamento">
            <NativeSelect value={f.kind || "ritiro"} onChange={set("kind")} data-testid="booking-edit-kind"><option value="ritiro">Ritiro</option><option value="riconsegna">Riconsegna urna</option><option value="promemoria">Appuntamento / promemoria</option></NativeSelect>
          </Field>
          <Field label="Orario (solo tu lo vedi/modifichi)"><Input type="time" value={f.orario || ""} onChange={set("orario")} className="bg-white" data-testid="booking-edit-orario" /></Field>
          <Field label="Giorno *"><Input type="date" value={f.date || ""} onChange={set("date")} className="bg-white" data-testid="booking-edit-date" /></Field>
          <Field label="Ritiro notturno (dopo le 18)">
            <label className="flex items-center gap-2 h-10 text-sm"><Checkbox checked={!!f.notturno} onCheckedChange={(v) => setF((p) => ({ ...p, notturno: !!v }))} data-testid="booking-edit-notturno" />Sì, +150€ + IVA</label>
          </Field>
          <Field label="Nome proprietario"><Input value={f.owner_name || ""} onChange={set("owner_name")} className="bg-white" data-testid="booking-edit-owner" /></Field>
          <Field label="Telefono"><Input value={f.owner_phone || ""} onChange={set("owner_phone")} className="bg-white" data-testid="booking-edit-phone" /></Field>
          <Field label={f.kind === "riconsegna" ? "Indirizzo di riconsegna *" : "Indirizzo di ritiro *"}><Input value={f.pickup_address || ""} onChange={set("pickup_address")} className="bg-white" data-testid="booking-edit-address" /></Field>
          <Field label="Città"><Input value={f.city || ""} onChange={set("city")} className="bg-white" /></Field>
          <Field label="Nome animale"><Input value={f.pet_name || ""} onChange={set("pet_name")} className="bg-white" /></Field>
          <Field label="Specie"><Input value={f.species || ""} onChange={set("species")} className="bg-white" /></Field>
          <Field label="Peso (kg)"><Input type="number" step="0.1" value={f.weight_kg ?? ""} onChange={set("weight_kg")} className="bg-white" /></Field>
          <Field label="Servizio">
            <NativeSelect value={f.service_type || "singola"} onChange={set("service_type")} data-testid="booking-edit-service">
              {Object.entries(SERVICES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </NativeSelect>
          </Field>
          <Field label="Note" className="sm:col-span-2"><Textarea rows={2} value={f.notes || ""} onChange={set("notes")} className="bg-white" /></Field>
        </div>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">Annulla</Button>
          <Button onClick={save} disabled={busy} data-testid="booking-edit-save" className="rounded-full bg-[#0D3B52] hover:bg-[#082535]">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva modifiche"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
